import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export type BomComponentType = "RAW" | "SEMI_FINISHED";

export interface BomDetailResponse {
    bom_detail_id?: number | null;
    bom_id?: number | null;
    line_no?: number | null;
    component_code: string;
    component_type?: BomComponentType | null;
    qty: number;
    uom: string;
    wastage_percent?: number | null;
    wastage_qty?: number | null;
    total_qty?: number | null;
    unit_cost?: number | null;
    total_cost?: number | null;
    sequence_no?: number | null;
    active?: number | null;
    created_at?: string | null;
    created_by?: string | null;
    updated_at?: string | null;
    updated_by?: string | null;
}

export interface RestaurantBomResponse {
    bom_id: number;
    bom_code?: string | null;
    item_code?: string | null;
    bom_name: string;
    version_no?: number | null;
    status?: number | null;
    yield_qty?: number | null;
    yield_uom: string;
    effective_from?: string | null;
    effective_to?: string | null;
    total_cost?: number | null;
    created_at?: string | null;
    created_by?: string | null;
    updated_at?: string | null;
    updated_by?: string | null;
    details: BomDetailResponse[];
}

export interface BomDetailLineInput {
    component_code: string;
    component_type?: BomComponentType;
    qty: number;
    uom: string;
    wastage_percent?: number;
    unit_cost?: number;
    line_no?: number;
    sequence_no?: number;
}

export interface RestaurantBomCreateRequest {
    item_code: string;
    bom_name: string;
    yield_qty?: number;
    yield_uom: string;
    effective_from?: string | null;
    effective_to?: string | null;
    details: BomDetailLineInput[];
}

export type RestaurantBomUpdateRequest = Omit<RestaurantBomCreateRequest, "item_code">;

const extractErrorMessage = (error: any, fallback: string): string => {
    if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        return typeof detail === "string" ? detail : JSON.stringify(detail);
    }
    return fallback;
};

export const getAllBoms = async (filters: { item_code?: string; status?: number } = {}): Promise<RestaurantBomResponse[]> => {
    try {
        const response: ApiResponse<RestaurantBomResponse[] | null> = (
            await apiClient.get("/restaurant_bom/all", {
                params: filters,
                headers: { "x-skip-auth-redirect": "true" },
            })
        ).data;
        return response.data || [];
    } catch (error: any) {
        throw new Error(extractErrorMessage(error, "Failed to fetch BOMs"));
    }
};

export const getActiveBom = async (itemCode: string): Promise<RestaurantBomResponse | null> => {
    try {
        const response: ApiResponse<RestaurantBomResponse | null> = (
            await apiClient.get(`/restaurant_bom/item/${encodeURIComponent(itemCode)}/active`, {
                headers: { "x-skip-auth-redirect": "true" },
            })
        ).data;
        return response.data || null;
    } catch (error: any) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw new Error(extractErrorMessage(error, `Failed to fetch active BOM for ${itemCode}`));
    }
};

export const getBomVersions = async (itemCode: string): Promise<RestaurantBomResponse[]> => {
    try {
        const response: ApiResponse<RestaurantBomResponse[] | null> = (
            await apiClient.get(`/restaurant_bom/item/${encodeURIComponent(itemCode)}/versions`, {
                headers: { "x-skip-auth-redirect": "true" },
            })
        ).data;
        return response.data || [];
    } catch (error: any) {
        throw new Error(extractErrorMessage(error, `Failed to fetch BOM versions for ${itemCode}`));
    }
};

export const createBom = async (payload: RestaurantBomCreateRequest): Promise<RestaurantBomResponse> => {
    try {
        const response: ApiResponse<RestaurantBomResponse> = (
            await apiClient.post("/restaurant_bom/create", payload, {
                headers: { "x-skip-auth-redirect": "true" },
            })
        ).data;
        if (!response.data) {
            throw new Error("Failed to create BOM - no data returned");
        }
        return response.data;
    } catch (error: any) {
        throw new Error(extractErrorMessage(error, "Failed to create BOM"));
    }
};

export const updateBom = async (bomId: number, payload: RestaurantBomUpdateRequest): Promise<RestaurantBomResponse> => {
    try {
        const response: ApiResponse<RestaurantBomResponse> = (
            await apiClient.put(`/restaurant_bom/update/${bomId}`, payload, {
                headers: { "x-skip-auth-redirect": "true" },
            })
        ).data;
        if (!response.data) {
            throw new Error("Failed to update BOM - no data returned");
        }
        return response.data;
    } catch (error: any) {
        throw new Error(extractErrorMessage(error, "Failed to update BOM"));
    }
};
